import { config } from "@/app/config";
import { OnboardUserModal } from "@/features/onboarding/components/OnboardUserModal";
import { useListUsersQuery } from "@/features/users/api/usersApi";
import { useHasPermission } from "@/shared/auth/usePermissions";
import { ErrorState } from "@/shared/components/ErrorState";
import { formatDateTime, formatRelative } from "@/shared/lib/datetime";
import { Permission } from "@keystone/contracts/shared";
import type { UserResponse, UserSort } from "@keystone/contracts/users";
import {
  ActionIcon,
  Anchor,
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { DataTable, type DataTableSortStatus } from "mantine-datatable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEdit2, FiRefreshCw, FiSearch, FiUserPlus } from "react-icons/fi";
import { Link } from "react-router";

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

type ColumnAccessor = "email" | "lastLogin";

const SORT_MAP: Record<`${ColumnAccessor}:${"asc" | "desc"}`, UserSort> = {
  "email:asc": "email:asc",
  "email:desc": "email:desc",
  "lastLogin:asc": "last_login:asc",
  "lastLogin:desc": "last_login:desc",
};

export function UsersPage() {
  const { t, i18n } = useTranslation("users");
  const canManageUsers = useHasPermission(Permission.ManageUsers);
  const [onboardOpened, { open: openOnboard, close: closeOnboard }] = useDisclosure(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(15);
  const [sortStatus, setSortStatusRaw] = useState<DataTableSortStatus<UserResponse>>({
    columnAccessor: "email",
    direction: "asc",
  });

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };
  const setPageSize = (size: number) => {
    setPageSizeRaw(size);
    setPage(1);
  };
  const setSortStatus = (status: DataTableSortStatus<UserResponse>) => {
    setSortStatusRaw(status);
    setPage(1);
  };

  const sort: UserSort =
    SORT_MAP[
      `${sortStatus.columnAccessor as ColumnAccessor}:${sortStatus.direction}` as keyof typeof SORT_MAP
    ] ?? "email:asc";

  const queryArgs = useMemo(
    () => ({
      page,
      limit: pageSize,
      sort,
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
    }),
    [page, pageSize, sort, debouncedSearch],
  );

  const { data, isLoading, isFetching, error, refetch } = useListUsersQuery(queryArgs);

  const total = data?.total ?? 0;
  const records = data?.items ?? [];

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" gap="sm" wrap="wrap">
        <Title order={2}>{t("page.title")}</Title>
        <Group gap="sm" wrap="nowrap" w={{ base: "100%", sm: "auto" }}>
          <TextInput
            leftSection={<FiSearch aria-hidden="true" />}
            placeholder={t("filter.placeholder")}
            value={search}
            onChange={(e) => handleSearch(e.currentTarget.value)}
            aria-label={t("filter.placeholder")}
            flex={{ base: 1, sm: "0 0 auto" }}
          />
          <Tooltip label={t("actions.reload")}>
            <ActionIcon
              variant="default"
              size="lg"
              loading={isFetching}
              onClick={() => void refetch()}
              aria-label={t("actions.reload")}
            >
              <FiRefreshCw aria-hidden="true" />
            </ActionIcon>
          </Tooltip>
          {canManageUsers && (
            <>
              <Button
                leftSection={<FiUserPlus aria-hidden="true" />}
                onClick={openOnboard}
                visibleFrom="sm"
              >
                {t("actions.onboard")}
              </Button>
              <Tooltip label={t("actions.onboard")} hiddenFrom="sm">
                <ActionIcon
                  variant="filled"
                  size="lg"
                  onClick={openOnboard}
                  aria-label={t("actions.onboard")}
                  hiddenFrom="sm"
                >
                  <FiUserPlus aria-hidden="true" />
                </ActionIcon>
              </Tooltip>
            </>
          )}
        </Group>
      </Group>

      {canManageUsers && <OnboardUserModal opened={onboardOpened} onClose={closeOnboard} />}

      {error ? (
        <ErrorState message={t("errors.listFailed")} onRetry={() => void refetch()} />
      ) : (
        <Card withBorder padding={0}>
          <DataTable<UserResponse>
            minHeight={300}
            withTableBorder={false}
            borderRadius="sm"
            highlightOnHover
            striped
            fetching={isLoading || isFetching}
            noRecordsText={
              debouncedSearch.trim() ? t("empty.filtered.description") : t("empty.description")
            }
            records={records}
            idAccessor="id"
            totalRecords={total}
            recordsPerPage={pageSize}
            page={page}
            onPageChange={setPage}
            recordsPerPageOptions={PAGE_SIZE_OPTIONS}
            onRecordsPerPageChange={setPageSize}
            sortStatus={sortStatus}
            onSortStatusChange={setSortStatus}
            columns={[
              {
                accessor: "email",
                title: t("table.email"),
                sortable: true,
                render: (user) => <Text size="sm">{user.email}</Text>,
              },
              {
                accessor: "lastLogin",
                title: t("table.lastLogin"),
                sortable: true,
                render: (user) =>
                  user.lastLogin ? (
                    <Tooltip label={formatDateTime(user.lastLogin, i18n.language)}>
                      <Text size="sm">{formatRelative(user.lastLogin, i18n.language)}</Text>
                    </Tooltip>
                  ) : (
                    <Text c="dimmed" size="sm">
                      {t("table.lastLoginNever")}
                    </Text>
                  ),
              },
              {
                accessor: "id",
                title: t("table.auth0Id"),
                render: (user) => (
                  <Anchor
                    href={`https://manage.auth0.com/dashboard/ca/${config.auth0TenantId}/users/${encodeURIComponent(user.id)}`}
                    target="_blank"
                    rel="noreferrer"
                    ff="monospace"
                    size="sm"
                  >
                    {user.id}
                  </Anchor>
                ),
              },
              {
                accessor: "actions",
                title: "",
                width: "0%",
                render: (user) => (
                  <Tooltip label={t("actions.edit")}>
                    <ActionIcon
                      variant="subtle"
                      component={Link}
                      to={`/users/${encodeURIComponent(user.id)}`}
                      aria-label={t("actions.edit")}
                    >
                      <FiEdit2 aria-hidden="true" />
                    </ActionIcon>
                  </Tooltip>
                ),
              },
            ]}
          />
        </Card>
      )}
    </Stack>
  );
}
